import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Field, Select } from '@/components/ui/Field'
import { Sheet } from '@/components/ui/Sheet'
import { useOperationsOwners } from '@/features/directories/api/useOperationsOwners'
import { useIssuingEntityOptions } from '@/features/finance/api/useFinance'
import { CreditDaysField } from '@/features/quotes/components/CreditDaysField'
import { conversionPreview } from '@/features/quotes/conversionPreview'
import { creditDaysPayload, isBlankOrCreditDays } from '@/features/quotes/quoteDraft'
import {
  convertErrorMessage,
  convertOwnerError,
  creditDaysError,
  paymentTermsText,
} from '@/features/quotes/quoteText'
import { useRoleLabel } from '@/features/settings/api/useRoleNames'
import { placeName } from '@/lib/countries'
import { formatMoney } from '@/lib/format'
import { modeLabelKey } from '@/lib/icons'
import { describeProblem } from '@/lib/problems'
import { transitRange } from '@/lib/transit'
import type { QuoteDetail, QuoteTeamMember } from '@/types/domain'

/* The confirmation in front of the quote's one irreversible act (QW-11,
 * walkthrough C-1).
 *
 * Converting writes a case, its rate, its planned costs and its first leg in
 * one server transaction, closes the quote and archives its chat. It used to
 * fire on a single press of a button labelled as though it only recorded the
 * client's answer, so the first look anybody had at the case was the case —
 * including whatever it had silently NOT received. This sheet is that look,
 * taken before the press: who the case is for, on what terms, who will work it,
 * and — the lines that matter most — what it will be missing.
 *
 * A Sheet with its own footer rather than a FormSheet, for the reason
 * CaseLifecycleActions' confirmation is one: nothing here is typed, so there is
 * no form, no draft and no dirty state, and the primary action is a decision
 * rather than a save.
 *
 * It STAYS OPEN while the request runs. Cancel is disabled and every dismissal
 * is ignored until the server answers, so a refusal lands here — beside the
 * preview that explains it — rather than on a page the reader has already
 * looked away from. On success the page navigates to the case, and the sheet
 * goes with it.
 *
 * The preview is `conversionPreview`, a display mirror of the server's rules,
 * and is the sheet's only source: nothing below re-derives a fact of its own.
 *
 * ── The Operations owner (S-1, owner decision O2) ───────────────────────────
 *
 * The one thing the sheet ASKS. A case is not created without somebody in
 * Operations to run it: the server refuses the request without an owner (422
 * `operationsOwnerId: required`), staffs the chosen person on the case team
 * and sends them a `case.assigned` bell entry. The list is the server's
 * (`useOperationsOwners`) and the rule behind it is too — the client offers
 * what it is served and re-checks nothing.
 *
 * Still a Sheet rather than a FormSheet: the choice is one pick from a list,
 * re-made in one press, so a discard confirmation over it would be the
 * confirmation that appears over nothing. The pick is held here and cleared
 * each time the sheet opens.
 *
 * ── The credit days (S-12) ──────────────────────────────────────────────────
 *
 * A CREDIT quote converts into a `credit` case with its day count. One that
 * states none — saved before the quote asked — is the second thing the sheet
 * ASKS: «Дней отсрочки», 1–365, sent as the body's `paymentTermsCreditDays`.
 * The server refuses the conversion without it (422 `required`, no case
 * created), and that refusal lands under the field. Cleared on open with the
 * owner.
 */
export function ConvertQuoteSheet({
  open,
  onClose,
  onConfirm,
  onOpenCase,
  pending,
  error,
  record,
  team,
}: {
  open: boolean
  onClose: () => void
  /** Asks for the case, with the Operations owner the reader chose and — on a
   *  CREDIT quote that states no days — the credit days typed here (S-12). */
  onConfirm: (operationsOwnerId: string, paymentTermsCreditDays?: number) => void
  /** Goes to the case, offered once the record says it is Converted and names
   *  one — the state a lost race re-reads the quote into. */
  onOpenCase: () => void
  /** The conversion request is in flight. */
  pending: boolean
  /** The mutation's refusal, RAW: an owner refusal is worded under the picker
   *  (`convertOwnerError`), anything else in the banner (`convertErrorMessage`). */
  error?: unknown
  record: QuoteDetail
  /** The quote's team, or undefined while it is loading or failed — which the
   *  sheet renders as no team row at all, never as an empty team. */
  team: QuoteTeamMember[] | undefined
}) {
  const { t, i18n } = useTranslation()
  /* Session-scoped and active entities only (see the hook), and fetched only
     while the sheet is open: a quote visit that never converts has no use for
     the list. */
  const { data: entities } = useIssuingEntityOptions({ enabled: open })
  /* The people who may run the case, and their roles by name (a custom
     dispatcher role holding `case.operations.lead` included) — both read only
     while the sheet is open, like the entities. */
  const owners = useOperationsOwners({ enabled: open })
  const roleName = useRoleLabel({ enabled: open })

  /* The pick, cleared whenever the sheet opens: a second visit starts from no
     choice rather than from one made for another moment. Reset during render
     rather than in an effect, the pattern `useViewState` documents — an effect
     would paint one frame of the old choice. */
  const [ownerId, setOwnerId] = useState('')
  const [creditDays, setCreditDays] = useState('')
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setOwnerId('')
      setCreditDays('')
    }
  }

  const ownerOptions = (owners.data ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    hint: roleName(person.role),
  }))
  /* The choice counts only while the served list still offers it. After a
     `not-assignable` / `not-operations` refusal useConvertQuote re-reads the
     list, the refused person drops out of it, and the press is refused again
     here rather than resending somebody the server has just turned down. */
  const chosen = ownerOptions.some((option) => option.value === ownerId) ? ownerId : ''

  /* The list's state, from the query STATUS rather than from the row count
     (failure-modes: a picker's empty state read off `.data`). Loading says so
     in the trigger; a failed read says what failed; only a read that
     SUCCEEDED with nobody says that nobody can be named. */
  const nobodyEligible = owners.isSuccess && ownerOptions.length === 0
  const ownerHint = owners.isError
    ? t(describeProblem(owners.error).detailKey)
    : nobodyEligible
      ? t('quotes.convert.owner.empty')
      : t('quotes.convert.owner.hint')

  /* An owner refusal is the picker's and is worded under it, once; anything
     else — a conflict, a malformed body, the network — stays in the banner. */
  const ownerError = convertOwnerError(error, t)
  /* The credit days' refusal is the field's, like the owner's is the picker's
     (S-12). */
  const daysError = creditDaysError(error, t)
  const bannerError =
    error && ownerError === undefined && daysError === undefined
      ? convertErrorMessage(error, t)
      : undefined

  /* Why «Create case» refuses, while it does. The empty list gets its own
     sentence: "choose first" over a list with nobody in it is an instruction
     the reader cannot follow. */
  const preview = conversionPreview(record, team, entities)

  /* The credit days, when the sheet asks for them: a whole number from 1 to
     365, which is the server's own range. */
  const daysAnswered =
    !preview.asksCreditDays ||
    (creditDays.trim() !== '' && isBlankOrCreditDays(creditDays))

  const createBlocked =
    chosen === ''
      ? nobodyEligible
        ? t('quotes.convert.owner.empty')
        : t('quotes.convert.owner.blocked')
      : !daysAnswered
        ? t('quotes.convert.creditDaysBlocked')
        : undefined

  /* Whether the create may still be offered. The page opens this sheet only on
     a Sent quote, but the record under it is re-read — on open, and after the
     `already-converted` / `not-convertible` refusals — and can come back
     Converted or Draft while the sheet is up. The SERVER's status, rendered;
     the server still refuses regardless (AD-21). */
  const convertible = record.status === 'sent'

  /* Every way out — the scrim, the header's close, Escape and Cancel — comes
     through here, and none of them may close a sheet whose request is still
     running. */
  const requestClose = () => {
    if (!pending) onClose()
  }

  const transit = transitRange(
    { min: preview.transitDaysMin, max: preview.transitDaysMax },
    t,
  )

  return (
    <Sheet
      open={open}
      onClose={requestClose}
      title={t('quotes.convert.title', { number: record.number })}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {convertible ? (
            <>
              <Button
                variant="secondary"
                size="lg"
                disabled={pending}
                onClick={requestClose}
                className="sm:w-auto"
              >
                {t('common.actions.cancel')}
              </Button>
              {/* Two props, two meanings: the refusal the reader can act on
                  (no owner yet) is `blockedReason`, the request in flight is
                  `disabled`. */}
              <Button
                size="lg"
                disabled={pending}
                blockedReason={createBlocked}
                onClick={() => {
                  if (chosen === '' || !daysAnswered) return
                  /* The days only when asked: every other quote's body stays
                     exactly the owner. */
                  const days = preview.asksCreditDays ? creditDaysPayload(creditDays) : null
                  if (days === null) onConfirm(chosen)
                  else onConfirm(chosen, days)
                }}
                className="sm:w-auto"
              >
                {pending ? t('quotes.actions.converting') : t('quotes.convert.confirm')}
              </Button>
            </>
          ) : (
            <>
              {/* Close, not Cancel: there is nothing left to cancel. */}
              <Button
                variant="secondary"
                size="lg"
                onClick={requestClose}
                className="sm:w-auto"
              >
                {t('common.actions.close')}
              </Button>
              {/* The page header's own way into the case, offered here because
                  the reader is looking here — and only when the record names
                  the case, rather than a button to nowhere. */}
              {record.status === 'converted' && record.caseId ? (
                <Button size="lg" onClick={onOpenCase} className="sm:w-auto">
                  {t('quotes.actions.openCase', { number: record.caseNumber ?? '' })}
                </Button>
              ) : null}
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Above the preview, where the eye lands when the sheet answers. */}
        {bannerError ? (
          <div role="alert">
            <Banner tone="negative" title={bannerError} />
          </div>
        ) : null}

        {/* A quote that stopped being Sent while the sheet was open gets no
            preview: it would describe a case that is not going to be created.
            The refusal, when there is one, already says why; otherwise this
            sentence does. */}
        {!convertible && !error ? (
          <Banner tone="info" title={t('quotes.convert.notSent')} />
        ) : null}

        {convertible ? (
          <>
            <p className="text-text-body text-[13px] leading-snug">
              {t('quotes.convert.intro')}
            </p>

            <dl className="border-line flex flex-col gap-2.5 rounded-lg border px-4 py-3">
              <PreviewRow label={t('quotes.convert.client')}>{preview.clientName}</PreviewRow>

              {/* Omitted, not dashed, when the entity is not among the active ones:
                  the sheet cannot tell a deactivated entity from one it could not
                  list, and the server's own refusal names the first. */}
              {preview.entityName ? (
                <PreviewRow label={t('quotes.convert.entity')}>{preview.entityName}</PreviewRow>
              ) : null}

              <PreviewRow label={t('quotes.convert.paymentTerms')}>
                <span>
                  {/* A credit term reads with its days (S-12) — «Кредит, 30
                      дней» — which is what the case will carry. */}
                  {paymentTermsText(
                    {
                      paymentTerms: preview.paymentTerms,
                      paymentTermsCreditDays: preview.paymentTermsCreditDays,
                    },
                    t,
                  ) ?? t('common.empty')}
                </span>
                {/* The caveat follows the RATE as well as the terms: CreateInTx
                    registers the rate and its instalments together, so with no
                    rate there are none — "one instalment dated at registration"
                    would be a promise the case does not keep. */}
                {preview.paymentTermsCarried && preview.rate ? null : (
                  <span className="text-text-muted mt-0.5 block text-[12px] leading-snug font-light">
                    {!preview.rate
                      ? preview.paymentTermsCarried
                        ? t('quotes.convert.termsNoRate')
                        : t('quotes.convert.termsCaveatNoRate')
                      : t('quotes.convert.termsCaveat')}
                  </span>
                )}
              </PreviewRow>

              <PreviewRow label={t('quotes.convert.incoterm')}>
                {preview.incoterm ?? t('common.empty')}
              </PreviewRow>

              {/* A dash for no mode, never «Мультимодал»: that fallback belongs to
                  a leg, whose mode is NOT NULL (types/domain.ts CaseSummary.mode). */}
              <PreviewRow label={t('quotes.convert.mode')}>
                {preview.mode ? t(modeLabelKey(preview.mode)) : t('common.empty')}
              </PreviewRow>

              <PreviewRow label={t('quotes.convert.transit')}>
                {transit ?? t('common.empty')}
              </PreviewRow>

              <PreviewRow label={t('quotes.convert.leg')}>
                {preview.leg === 'planned' && preview.route
                  ? t('quotes.convert.legPlanned', {
                      origin: placeName(preview.route.origin, i18n.language) ?? t('common.empty'),
                      destination:
                        placeName(preview.route.destination, i18n.language) ?? t('common.empty'),
                    })
                  : preview.leg === 'no-mode'
                    ? t('quotes.convert.legNoMode')
                    : t('quotes.convert.legNoOption')}
              </PreviewRow>

              {/* The sell total as served — formatted, never recomputed (AD-9). */}
              <PreviewRow label={t('quotes.convert.rate')}>
                {preview.rate ? (
                  <span className="asa-num">
                    {formatMoney(preview.rate.amount, preview.rate.currency)}
                  </span>
                ) : (
                  t('quotes.convert.noRate')
                )}
              </PreviewRow>

              <PreviewRow label={t('quotes.convert.plannedCosts')}>
                {preview.plannedLines > 0
                  ? t('quotes.convert.plannedLines', { count: preview.plannedLines })
                  : t('quotes.convert.noPlannedLines')}
              </PreviewRow>

              {/* Not drawn while the team is unknown: an empty row would say the
                  case gets nobody, which is not what an unread team means. */}
              {preview.team ? (
                <PreviewRow label={t('quotes.convert.team')}>
                  {preview.team.length > 0 ? preview.team.join(', ') : t('common.empty')}
                </PreviewRow>
              ) : null}
            </dl>

            {/* Who runs the case (S-1). Below the preview and above the
                sentence about who stays behind, which names this choice. The
                loading state lives in the trigger; the hint line carries the
                failed and the empty list. */}
            <Field
              label={t('quotes.convert.owner.label')}
              required
              error={ownerError}
              hint={ownerHint}
            >
              {(props) => (
                <Select
                  value={chosen}
                  onChange={setOwnerId}
                  options={ownerOptions}
                  placeholder={
                    owners.isPending ? t('common.loading') : t('common.form.selectPlaceholder')
                  }
                  searchable
                  disabled={pending}
                  {...props}
                />
              )}
            </Field>

            {/* A credit quote that states no days (S-12): the case cannot be
                credit without them, so they are asked here, with the same
                field and the same refusal wording as the quote's own. */}
            {preview.asksCreditDays ? (
              <div className="flex flex-col gap-1.5">
                <p className="text-text-muted text-[12.5px] leading-snug">
                  {t('quotes.convert.creditDaysAsk')}
                </p>
                <CreditDaysField
                  value={creditDays}
                  onChange={setCreditDays}
                  serverError={daysError}
                  disabled={pending}
                />
              </div>
            ) : null}

            {/* Who is left behind, said before rather than found after. */}
            <p className="text-text-muted text-[12.5px] leading-snug">
              {t('quotes.convert.notJoining')}
            </p>

            <Banner tone="attention" title={t('quotes.convert.irreversibleTitle')}>
              {t('quotes.convert.irreversible')}
            </Banner>
          </>
        ) : null}
      </div>
    </Sheet>
  )
}

/** One fact about the case-to-be. Stacked on a phone, label beside value
 *  above it — a sentence-long value would otherwise wrap into a column of
 *  three words. */
function PreviewRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:grid sm:grid-cols-[9.5rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-text-muted text-[12.5px] leading-snug font-light">{label}</dt>
      <dd className="text-text-body min-w-0 text-[13px] leading-snug">{children}</dd>
    </div>
  )
}
